import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiFetch } from '@/lib/api/client';
import { queryKeys } from '@/lib/queryKeys';

/**
 * AI Agent 대화 세션 — 서버 저장.
 *
 * 나눈 대화를 저장하고 나중에 다시 여는 기능이다. localStorage 는 그 브라우저에만 남아 기기 간에
 * 이어지지 않으므로, 백엔드(`/api/v1/agent-chat/sessions`)에 담는다. 여기서 하는 일은 다른 화면들과 같은
 * 모양이다 — `apiFetch` 로 조회·저장하고 TanStack Query 가 캐시·무효화를 맡는다.
 */

export type AgentChatRole = 'user' | 'assistant';

/** 답변 평가. 1 = 도움됨, -1 = 도움 안 됨, null = 없음. 사용자 말에는 늘 null 이다. */
export type AgentChatRating = 1 | -1 | null;

export type AgentChatMessage = {
  id: string;
  role: AgentChatRole;
  content: string;
  rating?: number | null;
  createdAt: string;
};

/** 사이드바 목록 한 줄. 말은 싣지 않는다. */
export type AgentChatSessionSummary = {
  id: string;
  title: string;
  createdAt: string;
  updatedAt: string;
};

/** 대화 하나를 열었을 때 — 껍데기와 그 안의 말 전부. */
export type AgentChatSessionDetail = AgentChatSessionSummary & {
  messages: AgentChatMessage[];
};

/** 첫 사용자 메시지가 들어오기 전까지의 이름. 백엔드의 같은 상수와 맞춰 둔다. */
export const DEFAULT_SESSION_TITLE = '새 대화';

// 경로가 `/v1/ai*` 가 아닌 이유: `/api/v1/ai*` 는 LLM 추론을 맡는 agent 서비스(:8000)로 프록시된다
// (vite.config.ts). 대화 저장은 DB·인증을 가진 Spring 백엔드(:8080)의 몫이라 그 네임스페이스를 피한다.
const BASE = '/v1/agent-chat/sessions';

/** 최근에 손댄 대화가 위로. 서버가 이미 그 순서로 주지만, 낙관적 갱신이 끼면 흔들릴 수 있어 한 번 더 맞춘다. */
export function sortByRecent<T extends AgentChatSessionSummary>(sessions: T[]): T[] {
  return [...sessions].sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
}

export type AgentSessionsApi = {
  sessions: AgentChatSessionSummary[];
  isLoading: boolean;
  activeId: string | null;
  activeSession: AgentChatSessionDetail | undefined;
  isSessionLoading: boolean;
  select: (id: string) => void;
  /** 열린 대화 없이 새로 시작하는 상태로 돌아간다. 실제 대화는 첫 메시지를 보낼 때 서버에 만들어진다. */
  clearActive: () => void;
  /** 빈 대화를 서버에 만들고 그 id 를 돌려준다. */
  createSession: () => Promise<string>;
  appendMessage: (sessionId: string, input: { role: AgentChatRole; content: string }) => Promise<AgentChatSessionDetail>;
  renameSession: (sessionId: string, title: string) => void;
  deleteSession: (sessionId: string) => void;
  /** 답변에 👍/👎. 같은 것을 다시 누르면 `null` 로 되돌린다 — 그 판단은 부르는 쪽이 한다. */
  rateMessage: (sessionId: string, messageId: string, rating: AgentChatRating) => void;
};

/**
 * 세션 목록과 활성 세션을 관리하는 훅.
 *
 * 활성 대화는 처음엔 없다(빈 화면 = 새 대화). 사용자가 목록에서 고르거나 첫 메시지를 보내면 정해진다.
 * 지워진 대화가 열려 있었다면 자동으로 목록에서 손을 뗀다.
 *
 * ## `enabled`
 *
 * 이 훅은 이제 {@link AgentChatProvider} 안에서 **앱 전역에** 산다(대화가 화면 이동에 죽지 않게).
 * 늘 켜 두면 AI 를 열지도 않은 사람의 모든 화면에서 목록 조회가 한 번씩 나가므로, 도크나 AI 탭이
 * 처음 열릴 때 켠다. 한 번 켜지면 그 세션 동안 다시 끄지 않는다 — 껐다 켜면 캐시가 있는데도
 * 목록이 빈 채로 한 번 깜빡인다.
 */
/*
  ★ **「마지막으로 보던 대화 되살리기」는 걷어냈다**(2026-09-01).

  한때 `sessionStorage` 에 활성 대화 id 를 남겨 새로고침해도 하던 대화가 열리게 했다. 그런데
  같은 날 규칙이 하나 정해졌다 — **AI 를 열 때마다 새 대화로 시작한다**({@link AiAgentDock}).

  이력이 매 턴 통째로 실려 가므로, 앞 화면에서 받은 답이 다음 화면의 물음까지 따라붙기 때문이다.
  그 규칙이 서면 되살리기는 **반대 방향으로 당긴다** — 열 때 지우고 새로고침에 되살리면, 사용자는
  같은 동작에서 두 가지 결과를 보게 된다.

  대화 자체는 백엔드에 남는다(`ai_agent_chat_sessions`). 지난 대화는 **탭의 목록**에서 고르면
  그대로 열린다 — 되살리기를 뺀다고 잃는 것은 「자동으로」 하나뿐이다.
*/

export function useAgentSessions(enabled = true): AgentSessionsApi {
  const queryClient = useQueryClient();
  const [activeId, setActiveId] = useState<string | null>(null);

  const sessionsQuery = useQuery({
    queryKey: queryKeys.aiAgentSessions,
    queryFn: () => apiFetch<AgentChatSessionSummary[]>(BASE),
    enabled,
  });
  const sessions = sessionsQuery.data ?? [];

  const sessionQuery = useQuery({
    queryKey: [...queryKeys.aiAgentSession, activeId],
    queryFn: () => apiFetch<AgentChatSessionDetail>(`${BASE}/${activeId}`),
    enabled: enabled && activeId != null,
  });

  // 목록이 갱신됐는데 열려 있던 대화가 사라졌으면(다른 곳에서 삭제 등) 손을 뗀다.
  useEffect(() => {
    if (activeId && sessionsQuery.data && !sessionsQuery.data.some((session) => session.id === activeId)) {
      setActiveId(null);
    }
  }, [activeId, sessionsQuery.data]);

  const invalidateSessions = () => queryClient.invalidateQueries({ queryKey: queryKeys.aiAgentSessions });

  const createMutation = useMutation({
    mutationFn: () => apiFetch<AgentChatSessionSummary>(BASE, { method: 'POST' }),
    onSuccess: () => invalidateSessions(),
  });

  const appendMutation = useMutation({
    mutationFn: ({ sessionId, role, content }: { sessionId: string; role: AgentChatRole; content: string }) =>
      apiFetch<AgentChatSessionDetail>(`${BASE}/${sessionId}/messages`, { method: 'POST', body: { role, content } }),
    onSuccess: (detail) => {
      // 방금 받은 대화 본문을 캐시에 직접 심어 다시 받아오지 않는다. 목록은 제목·정렬이 바뀌므로 무효화한다.
      queryClient.setQueryData([...queryKeys.aiAgentSession, detail.id], detail);
      return invalidateSessions();
    },
  });

  const renameMutation = useMutation({
    mutationFn: ({ sessionId, title }: { sessionId: string; title: string }) =>
      apiFetch<AgentChatSessionSummary>(`${BASE}/${sessionId}`, { method: 'PATCH', body: { title } }),
    onSuccess: (_summary, { sessionId }) => {
      queryClient.invalidateQueries({ queryKey: [...queryKeys.aiAgentSession, sessionId] });
      return invalidateSessions();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (sessionId: string) => apiFetch<void>(`${BASE}/${sessionId}`, { method: 'DELETE' }),
    onSuccess: (_void, sessionId) => {
      if (activeId === sessionId) setActiveId(null);
      return invalidateSessions();
    },
  });

  /** 캐시에 든 대화 본문에서 말 하나의 평가만 바꾼다. 본문을 다시 받아올 이유가 없다 — 바뀐 것은 그 한 칸이다. */
  const patchRating = (sessionId: string, messageId: string, rating: number | null) => {
    queryClient.setQueryData<AgentChatSessionDetail>([...queryKeys.aiAgentSession, sessionId], (detail) =>
      detail
        ? { ...detail, messages: detail.messages.map((m) => (m.id === messageId ? { ...m, rating } : m)) }
        : detail,
    );
  };

  const rateMutation = useMutation({
    mutationFn: ({ sessionId, messageId, rating }: { sessionId: string; messageId: string; rating: AgentChatRating }) =>
      apiFetch<AgentChatMessage>(`${BASE}/${sessionId}/messages/${messageId}/rating`, { method: 'PATCH', body: { rating } }),
    // 누르는 즉시 칠한다. 평가는 답이 아니라 답에 대한 표시라 잠깐 앞서 그려도 잃는 것이 없고,
    // 서버가 거절하면(남의 대화·사용자 말) 그때 본문을 다시 받아 원래대로 돌린다.
    onMutate: ({ sessionId, messageId, rating }) => patchRating(sessionId, messageId, rating),
    onSuccess: (message, { sessionId }) => patchRating(sessionId, message.id, message.rating ?? null),
    onError: (_error, { sessionId }) =>
      queryClient.invalidateQueries({ queryKey: [...queryKeys.aiAgentSession, sessionId] }),
  });

  return {
    sessions,
    isLoading: sessionsQuery.isLoading,
    activeId,
    activeSession: sessionQuery.data,
    isSessionLoading: activeId != null && sessionQuery.isLoading,
    select: (id) => setActiveId(id),
    clearActive: () => setActiveId(null),
    createSession: async () => {
      const created = await createMutation.mutateAsync();
      setActiveId(created.id);
      return created.id;
    },
    appendMessage: (sessionId, input) => appendMutation.mutateAsync({ sessionId, ...input }),
    renameSession: (sessionId, title) => renameMutation.mutate({ sessionId, title }),
    deleteSession: (sessionId) => deleteMutation.mutate(sessionId),
    rateMessage: (sessionId, messageId, rating) => rateMutation.mutate({ sessionId, messageId, rating }),
  };
}
